(() => {
  const sectionSelector = '[data-section-type="product"]';
  const addonsSelector = '[data-product-addons]';
  const groupSelector = '[data-product-addons-group]';
  const itemSelector = '[data-product-addons-item]';
  const inputSelector = '[data-product-addons-input]';
  const addToCartSelector = '[data-add-to-cart]';
  const productFormSelector = 'form[data-product-form]';
  const addActionWrapperSelector = '[data-add-action-wrapper]';
  const errorSelector = '[data-add-action-errors]';
  const drawerSelector = '[data-drawer="drawer-cart"]';
  const cartPageSelector = '[data-ajax-disable]';
  const ocuHandlerFlag = 'frontlineProductAddonsHandlerInstalled';
  const ocuDrawerSelector = '.ocu-cart-drawer';
  const ocuDrawerVisibleClass = 'ocu-visible';
  const ocuDrawerWaitTimeout = 60000;

  class ProductAddonsSection {
    constructor(section) {
      this.section = section;
      this.boundChange = this.handleChange.bind(this);
      this.boundMouseOver = this.handleMouseOver.bind(this);
      this.boundMouseOut = this.handleMouseOut.bind(this);
      this.boundFocusIn = this.handleFocusIn.bind(this);
      this.boundFocusOut = this.handleFocusOut.bind(this);
      this.boundAddToCartClick = this.handleAddToCartClick.bind(this);
      this.selectedVariantIds = new Set(
        [...this.section.querySelectorAll(`${inputSelector}:checked`)].map((input) => input.value)
      );
      this.addToCartRequest = null;
      this.nativeReplayButtons = new WeakSet();
      this.pendingOcuDrawer = null;
      this.successTimer = null;

      this.init();
    }

    init() {
      this.section.addEventListener('change', this.boundChange);
      this.section.addEventListener('mouseover', this.boundMouseOver);
      this.section.addEventListener('mouseout', this.boundMouseOut);
      this.section.addEventListener('focusin', this.boundFocusIn);
      this.section.addEventListener('focusout', this.boundFocusOut);
      this.section.addEventListener('click', this.boundAddToCartClick, true);

      this.section.querySelectorAll(`${itemSelector} ${inputSelector}`).forEach((input) => {
        this.updateItem(input.closest(itemSelector), input.checked);
      });
      this.section.querySelectorAll(groupSelector).forEach((group) => this.updateLegend(group));
    }

    updateItem(item, isSelected) {
      const price = item.querySelector('[data-product-addons-price]');
      const includedLabel = item.closest(groupSelector)?.dataset.includedLabel || 'Included';

      if (price) {
        price.textContent = isSelected ? includedLabel : price.dataset.productAddonsPrice || '';
      }
    }

    updateLegend(group) {
      this.showLegendValue(group, '');
    }

    showLegendValue(group, value) {
      const target = group.querySelector('[data-product-addons-legend-value]');
      const legend = group.querySelector('.product-addons__legend');

      if (target) {
        target.textContent = value;
      }
      legend?.classList.toggle('product-addons__legend--has-value', Boolean(value));
      legend?.classList.toggle('product-addons__legend--empty', !value);
    }

    handleChange(event) {
      if (event.target.matches(inputSelector)) {
        const item = event.target.closest(itemSelector);
        const group = event.target.closest(groupSelector);

        if (event.target.checked) {
          this.selectedVariantIds.add(event.target.value);
        } else {
          this.selectedVariantIds.delete(event.target.value);
        }

        if (item && group) {
          this.updateItem(item, event.target.checked);
          this.updateLegend(group);
        }
      }
    }

    handleAddToCartClick(event) {
      const button = event.target.closest(addToCartSelector);
      if (!button || !this.section.contains(button)) return;

      const selectedVariantIds = this.getSelectedVariantIds();
      if (!selectedVariantIds.length) return;

      const form = button.closest(productFormSelector);
      if (!form || form.querySelector('[type="file"]')) return;

      if (this.isOcuHandlerAvailable()) return;

      event.preventDefault();
      event.stopImmediatePropagation();

      if (this.addToCartRequest) return;

      this.addToCartRequest = this.addProductsFallback(button, form, selectedVariantIds).finally(() => {
        this.addToCartRequest = null;
      });
    }

    handleOcuAddToCart(button) {
      if (!button || !this.section.contains(button)) return false;
      if (this.nativeReplayButtons.has(button)) return false;

      const selectedVariantIds = this.getSelectedVariantIds();
      if (!selectedVariantIds.length) return false;

      const form = button.closest(productFormSelector);
      if (!form || form.querySelector('[type="file"]')) return false;

      if (this.addToCartRequest) return true;

      this.addToCartRequest = this.addAddOnsBeforeNativeClick(button, selectedVariantIds).finally(() => {
        this.addToCartRequest = null;
      });

      return true;
    }

    getSelectedVariantIds() {
      const selectedVariantIds = [
        ...this.section.querySelectorAll(`${inputSelector}:checked:not(:disabled)`),
      ]
        .map((input) => Number(input.value))
        .filter((variantId) => Number.isSafeInteger(variantId) && variantId > 0);

      this.selectedVariantIds = new Set(selectedVariantIds.map(String));
      return [...new Set(selectedVariantIds)];
    }

    getCartRoot() {
      const root = window.Shopify?.routes?.root || window.theme?.routes?.root_url || '/';
      return root.endsWith('/') ? root : `${root}/`;
    }

    isOcuHandlerAvailable() {
      const ocu = window.Zipify?.OCU;
      return ocu?.enabled === true && Boolean(ocu.api?.[ocuHandlerFlag]);
    }

    buildAddOnsPayload(selectedVariantIds) {
      return {
        items: selectedVariantIds.map((id) => ({id, quantity: 1})),
      };
    }

    buildFallbackPayload(form, selectedVariantIds) {
      const formData = new FormData(form);
      const variantId = Number(formData.get('id'));
      const parsedQuantity = Number.parseInt(formData.get('quantity') || '1', 10);

      if (!Number.isSafeInteger(variantId) || variantId <= 0) {
        throw new Error(window.theme?.strings?.unavailable || '');
      }

      const mainItem = {
        id: variantId,
        quantity: Number.isSafeInteger(parsedQuantity) && parsedQuantity > 0 ? parsedQuantity : 1,
      };
      const sellingPlanValue = formData.get('selling_plan');
      const sellingPlanId = Number(sellingPlanValue);
      const properties = {};
      const attributes = {};

      if (sellingPlanValue && Number.isSafeInteger(sellingPlanId) && sellingPlanId > 0) {
        mainItem.selling_plan = sellingPlanId;
      }

      formData.forEach((value, key) => {
        const propertyMatch = key.match(/^properties\[(.+)\]$/);
        const attributeMatch = key.match(/^attributes\[(.+)\]$/);

        if (propertyMatch && typeof value === 'string') {
          properties[propertyMatch[1]] = value;
        }
        if (attributeMatch && typeof value === 'string') {
          attributes[attributeMatch[1]] = value;
        }
      });

      if (Object.keys(properties).length) {
        mainItem.properties = properties;
      }

      const payload = {
        items: [
          mainItem,
          ...selectedVariantIds.map((id) => ({id, quantity: 1})),
        ],
      };

      if (Object.keys(attributes).length) {
        payload.attributes = attributes;
      }

      return payload;
    }

    async requestCartAdd(payload) {
      const response = await fetch(`${this.getCartRoot()}cart/add.js`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Accept: 'application/json',
        },
        body: JSON.stringify(payload),
      });
      const responseData = await response.json().catch(() => null);

      if (!response.ok) {
        const error = new Error(responseData?.message || window.theme?.strings?.stockout || '');
        error.status = response.status;
        error.data = responseData;
        throw error;
      }

      return responseData;
    }

    setLoadingState(button) {
      window.clearTimeout(this.successTimer);
      button.disabled = true;
      button.classList.remove('has-success');
      button.classList.add('loading');
    }

    setSuccessState(button) {
      button.classList.remove('loading');
      button.classList.add('has-success');
      this.successTimer = window.setTimeout(() => {
        button.classList.remove('has-success');
        button.disabled = false;
      }, 3500);
    }

    setNativeClickState(button) {
      button.classList.remove('loading', 'has-success');
      button.disabled = false;
    }

    clearError(button) {
      button.closest(addActionWrapperSelector)?.querySelector(errorSelector)?.replaceChildren();
    }

    getErrorMessage(error) {
      const responseData = error.data;

      if (typeof responseData?.description === 'string') {
        return responseData.description;
      }
      if (responseData?.description && typeof responseData.description === 'object') {
        return Object.entries(responseData.description)
          .map(([key, value]) => `${key}: ${value}`)
          .join('\n');
      }
      if (typeof responseData?.message === 'string') {
        return responseData.message;
      }

      return error.message || window.theme?.strings?.stockout || '';
    }

    setErrorState(button, error) {
      const errorContainer = button.closest(addActionWrapperSelector)?.querySelector(errorSelector);
      const message = document.createElement('div');

      button.classList.remove('loading', 'has-success');
      button.disabled = false;

      if (!errorContainer) return;

      message.className = 'errors';
      message.setAttribute('role', 'alert');
      message.setAttribute('aria-live', 'assertive');
      message.textContent = this.getErrorMessage(error);
      errorContainer.replaceChildren(message);
    }

    resetOcuState() {
      window.Zipify?.OCU?.api?.store?.set?.('submitted', false);
    }

    cancelPendingOcuDrawer() {
      const pending = this.pendingOcuDrawer;
      if (!pending) return;

      this.pendingOcuDrawer = null;
      pending.abortController.abort();
      pending.errorObserver?.disconnect();
      window.clearTimeout(pending.timeoutId);
    }

    waitForNativeCartAndOpenOcuDrawer(button) {
      this.cancelPendingOcuDrawer();

      const abortController = new AbortController();
      const errorContainer = button.closest(addActionWrapperSelector)?.querySelector(errorSelector);
      const pending = {
        abortController,
        errorObserver: null,
        timeoutId: null,
      };
      const cleanup = () => {
        if (this.pendingOcuDrawer !== pending) return;
        this.cancelPendingOcuDrawer();
      };
      const openOcuDrawer = () => {
        const customDrawer = document.querySelector('[data-custom-cart-drawer]');
        if (customDrawer) {
          customDrawer.dispatchEvent(
            new CustomEvent('theme:drawer:open', {
              detail: {reinit: true},
              bubbles: true,
            })
          );
          cleanup();
          return;
        }

        if (!document.querySelector(`${ocuDrawerSelector}.${ocuDrawerVisibleClass}`)) {
          window.dispatchEvent(new CustomEvent('ocu:cart:open'));
        }
        cleanup();
      };

      this.pendingOcuDrawer = pending;
      document.addEventListener('theme:cart:change', openOcuDrawer, {
        once: true,
        signal: abortController.signal,
      });

      if (errorContainer) {
        pending.errorObserver = new MutationObserver(() => {
          if (errorContainer.querySelector('.errors') || errorContainer.textContent.trim()) {
            cleanup();
          }
        });
        pending.errorObserver.observe(errorContainer, {
          childList: true,
          subtree: true,
          characterData: true,
        });
      }

      pending.timeoutId = window.setTimeout(cleanup, ocuDrawerWaitTimeout);
    }

    async refreshCartState() {
      try {
        const response = await fetch(`${this.getCartRoot()}cart.js`, {
          headers: {Accept: 'application/json'},
        });
        if (!response.ok) return null;

        const cart = await response.json();
        document.dispatchEvent(
          new CustomEvent('theme:cart:change', {
            detail: {cart},
            bubbles: true,
          })
        );
        return cart;
      } catch {
        return null;
      }
    }

    notifyCart(responseData, cartWasRefreshed) {
      if (document.querySelector(cartPageSelector)) {
        document.dispatchEvent(new CustomEvent('theme:cart:reload', {bubbles: true}));
        return;
      }

      const drawer = document.querySelector(drawerSelector);
      if (!drawer) return;

      drawer.dispatchEvent(
        new CustomEvent('theme:drawer:open', {
          detail: {
            variant: responseData?.items?.[0] || responseData,
            reinit: !cartWasRefreshed,
          },
          bubbles: true,
        })
      );
    }

    async addAddOnsBeforeNativeClick(button, selectedVariantIds) {
      this.setLoadingState(button);
      this.clearError(button);

      try {
        await this.requestCartAdd(this.buildAddOnsPayload(selectedVariantIds));
        this.setNativeClickState(button);
        this.waitForNativeCartAndOpenOcuDrawer(button);
        this.nativeReplayButtons.add(button);
        try {
          button.click();
        } catch (error) {
          this.cancelPendingOcuDrawer();
          throw error;
        } finally {
          window.setTimeout(() => this.nativeReplayButtons.delete(button));
        }
      } catch (error) {
        this.resetOcuState();
        this.setErrorState(button, error);
      }
    }

    async addProductsFallback(button, form, selectedVariantIds) {
      this.setLoadingState(button);
      this.clearError(button);

      try {
        const payload = this.buildFallbackPayload(form, selectedVariantIds);
        const responseData = await this.requestCartAdd(payload);
        this.setSuccessState(button);
        const cart = await this.refreshCartState();
        this.notifyCart(responseData, Boolean(cart));
      } catch (error) {
        this.setErrorState(button, error);
      }
    }

    handleMouseOver(event) {
      const item = event.target.closest(itemSelector);
      if (!item || !this.section.contains(item) || item.contains(event.relatedTarget)) return;

      const group = item.closest(groupSelector);
      if (group) this.showLegendValue(group, item.dataset.productAddonsTitle || '');
    }

    handleMouseOut(event) {
      const item = event.target.closest(itemSelector);
      if (!item || !this.section.contains(item) || item.contains(event.relatedTarget)) return;

      const group = item.closest(groupSelector);
      if (group && !item.contains(document.activeElement)) this.updateLegend(group);
    }

    handleFocusIn(event) {
      const item = event.target.closest(itemSelector);
      if (!item || !this.section.contains(item)) return;

      const group = item.closest(groupSelector);
      if (group) this.showLegendValue(group, item.dataset.productAddonsTitle || '');
    }

    handleFocusOut(event) {
      const item = event.target.closest(itemSelector);
      if (!item || !this.section.contains(item) || item.contains(event.relatedTarget)) return;

      const group = item.closest(groupSelector);
      if (group) this.updateLegend(group);
    }

    destroy() {
      this.section.removeEventListener('change', this.boundChange);
      this.section.removeEventListener('mouseover', this.boundMouseOver);
      this.section.removeEventListener('mouseout', this.boundMouseOut);
      this.section.removeEventListener('focusin', this.boundFocusIn);
      this.section.removeEventListener('focusout', this.boundFocusOut);
      this.section.removeEventListener('click', this.boundAddToCartClick, true);
      this.cancelPendingOcuDrawer();
      window.clearTimeout(this.successTimer);
    }
  }

  function initProductAddons(root = document) {
    const sections = [];
    if (root.matches?.(sectionSelector)) {
      sections.push(root);
    } else {
      sections.push(...(root.querySelectorAll?.(sectionSelector) || []));
    }

    sections.forEach((section) => {
      if (!section.querySelector(addonsSelector) || section.__productAddons) return;
      section.__productAddons = new ProductAddonsSection(section);
    });
  }

  const productAddonsBridge = window.FrontlineProductAddons || {};
  productAddonsBridge.handleOcuAddToCart = (button) => {
    if (!(button instanceof Element)) return false;

    const section = button.closest(sectionSelector);
    return Boolean(section?.__productAddons?.handleOcuAddToCart(button));
  };
  window.FrontlineProductAddons = productAddonsBridge;

  document.addEventListener('DOMContentLoaded', () => initProductAddons());
  document.addEventListener('shopify:section:load', (event) => initProductAddons(event.target));
  document.addEventListener('shopify:section:unload', (event) => {
    const sections = [];
    if (event.target.matches?.(sectionSelector)) {
      sections.push(event.target);
    } else {
      sections.push(...(event.target.querySelectorAll?.(sectionSelector) || []));
    }

    sections.forEach((section) => {
      section.__productAddons?.destroy();
      delete section.__productAddons;
    });
  });
})();

(() => {
  ((root, zipifyKey, apiKey) => {
    root[zipifyKey] = root[zipifyKey] || {};
    root[apiKey] = root[apiKey] || {};
    root[zipifyKey].OCU = root[zipifyKey].OCU || {api: root[apiKey]};
  })(window, 'Zipify', 'OCUApi');

  const api = window.Zipify.OCU.api;
  const installedFlag = 'frontlineProductAddonsHandlerInstalled';
  if (api[installedFlag]) return;

  const previousHandler = api.customAddToCartEventHandler;
  api.customAddToCartEventHandler = (button) => {
    if (window.FrontlineProductAddons?.handleOcuAddToCart(button)) return;

    if (typeof previousHandler === 'function') {
      previousHandler(button);
      return;
    }

    if (!button) return;
    button.disabled = false;
    button.click();
  };
  api[installedFlag] = true;
})();
